<script lang="ts">

// CSS for the dropdown menu (teleported to body, so can't use scoped styles)
// This is injected once globally when the first MCombobox mounts
const MENU_STYLES = `
.v-menu__content.m-combobox-menu .v-list-item {
  height: auto !important;
  min-height: 32px !important;
}
.v-menu__content.m-combobox-menu .v-list-item__content {
  padding: 0 !important;
}
.v-menu__content.m-combobox-menu .v-simple-checkbox {
  padding: 0 !important;
  margin-right: 8px !important;
}
.v-menu__content.m-combobox-menu .v-list-item__action {
  margin: 0 !important;
  margin-right: 8px !important;
}
`;

let stylesInjected = false;

function injectMenuStyles(): void {
  if (typeof document === 'undefined') return;
  const existingStyle = document.querySelector('style[data-mcombobox-menu-styles]');
  if (existingStyle) {
    stylesInjected = true;
    return;
  }
  if (stylesInjected) return;
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-mcombobox-menu-styles', '');
  styleEl.textContent = MENU_STYLES;
  document.head.appendChild(styleEl);
  stylesInjected = true;
}

import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { VCombobox } from 'vuetify/lib';
import {
  createFocusBlurHandlers,
  createListenersWithFocusHandlers,
} from '@mimir/web/utils/m-input-common';
import type { VuetifyInputRef } from '@mimir/web/utils/m-input-common';
import MLabelRow from './MLabelRow.vue';

export default defineComponent({
  name: 'MCombobox',
  components: {
    VCombobox,
    MLabelRow,
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: undefined,
    },
    highContrast: {
      type: Boolean,
      default: false,
    },
    closeOnClick: {
      type: Boolean,
      default: false,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    menuActive: { // For external control of menu active state (e.g. getting focus state with readonly and custom menu)
      type: Boolean,
      default: false,
    },
    menuProps: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    v2Style: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFocused: false,
      focusHandlers: null as ReturnType<typeof createFocusBlurHandlers> | null,
    };
  },
  computed: {
    isDefaultStyle(): boolean {
      return !this.v2Style;
    },
    isPlain(): boolean {
      if (this.isDefaultStyle) return false;
      return this.plain || (!this.solo && !this.flat && !this.dense && !this.compact && !this.outlined);
    },
    comboboxListeners(): Record<string, unknown> {
      return createListenersWithFocusHandlers(this.$listeners, this.focusHandlers);
    },
    filteredSlots(): Record<string, unknown> {
      const { infotext, ...filtered } = this.$slots;
      return filtered;
    },
  },
  mounted() {
    injectMenuStyles();
    this.setupHandlers();
  },
  methods: {
    setupHandlers() {
      this.focusHandlers = createFocusBlurHandlers({
        setFocused: (value) => { this.isFocused = value; },
        getListeners: () => this.$listeners,
      });
    },
    activateMenu() {
      const ref = this.$refs.combobox as VuetifyInputRef | undefined;
      ref?.activateMenu?.();
    },
    focus() {
      const ref = this.$refs.combobox as VuetifyInputRef | undefined;
      ref?.focus?.();
    },
    blur() {
      const ref = this.$refs.combobox as VuetifyInputRef | undefined;
      ref?.blur?.();
    },
    getIsMenuActive(): boolean {
      const ref = this.$refs.combobox as VuetifyInputRef | undefined;
      return ref?.isMenuActive ?? false;
    },
    setIsMenuActive(value: boolean) {
      const ref = this.$refs.combobox as VuetifyInputRef | undefined;
      if (ref && 'isMenuActive' in ref) {
        ref.isMenuActive = value;
      }
    },
    getComboboxRef() {
      return this.$refs.combobox;
    },
  },
});
</script>

<template>
  <div>
    <MLabelRow
      v-if="isDefaultStyle ? label : (!isPlain && label && (!outlined || solo))"
      :label="label"
      :class="[isDefaultStyle ? 'm-default-style-label' : 'profile-label', isFocused && 'focused', highContrast && 'high-contrast', !isDefaultStyle && outlined && solo && 'muted']"
    >
      <template
        v-if="$slots.infotext"
        #infotext
      >
        <slot name="infotext" />
      </template>
    </MLabelRow>
    <div
      role="combobox"
      :aria-label="$attrs['aria-label'] ?? $attrs.ariaLabel ?? label"
    >
      <VCombobox
      ref="combobox"
      :class="[
        isDefaultStyle ? 'm-combobox m-default-style' : (isPlain ? 'm-combobox' : 'm-combobox m-common'),
        !isDefaultStyle && label !== undefined && outlined && solo && 'm-combobox-inline-label',
        menuActive && 'm-combobox-menu-active',
        $attrs.class
      ]"
      :aria-label="$attrs['aria-label'] ?? $attrs.ariaLabel ?? label"
      v-bind="$attrs"
      :dense="isDefaultStyle ? false : (dense || compact)"
      :flat="isDefaultStyle ? true : (flat || (!isPlain && label !== undefined && !outlined))"
      :hide-details="$attrs['hide-details'] ?? 'auto'"
      :label="isDefaultStyle ? undefined : (outlined && solo ? undefined : label)"
      :loader-height="1"
      :menu-props="{ ...menuProps, contentClass: 'm-combobox-menu', offsetY: true }"
      :outlined="isDefaultStyle ? false : outlined"
      :solo="isDefaultStyle ? true : (outlined ? false : solo || (!isPlain && label !== undefined && !outlined))"
      v-on="comboboxListeners"
    >
      <template
        v-for="(_, scopedSlotName) in $scopedSlots"
        #[scopedSlotName]="slotData"
      >
        <slot
          :name="scopedSlotName"
          v-bind="slotData"
        />
      </template>
      <template
        v-for="(_, slotName) in filteredSlots"
        #[slotName]
      >
        <slot :name="slotName" />
      </template>
    </VCombobox>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.v-text-field {
  padding-top: 0 !important;
  margin-top: 0 !important;
}

.v-input {
  margin-bottom: 4px;

  :deep(.v-input__prepend-outer) {
    margin: 4px 4px 0 0 !important;
  }

  &.v-input--dense :deep(.v-input__prepend-outer) {
    .v-icon {
      font-size: 16px;
    }
  }

  :deep(.v-input__append-inner) {
    align-self: center;
    margin-top: 0px !important;
    .v-icon {
      font-size: 24px !important;
    }
  }

  :deep(.v-input__append-outer) {
    font-size: 24px !important;
    margin-top: 4px !important;
  }

  :deep(.v-input__icon--append-outer) button {
    color: var(--v-strong-base) !important;
  }

  :deep(.v-input__control) {
    min-height: 24px !important;
  }

  &:not(.m-checkbox):not(.v-text-field--outlined):not(.m-default-style) :deep(.v-input__slot) {
    font-size: 12px;
    background: var(--v-inputBackground-base) !important;
    outline: 1px solid var(--v-inputBorder-lighten2);
    padding: 0 8px !important;
    border-radius: 5px !important;

    .theme--light & {
      background: white !important;
      outline: 1px solid var(--v-inputBorder-darken1);
    }
  }

  &.v-text-field--outlined :deep(.v-input__slot) {
    font-size: 12px;
    min-height: 32px !important;
  }

  // Prevent Vuetify's outlined focus from thickening fieldset border (1px→2px)
  // which shifts content. Keep 1px and rely on color change for focus indicator.
  &.v-text-field--outlined :deep(fieldset) {
    border-width: 1px !important;
  }

  &.v-input--is-focused:not(.v-text-field--outlined):not(.m-default-style) :deep(.v-input__slot) {
    background: var(--v-inputFocusBackground-base) !important;
  }

  &.error--text:not(.v-text-field--outlined) :deep(.v-input__slot) {
    outline: 1px solid var(--v-error-base);
  }

  &.v-select--chips :deep(input) {
    max-height: 20px !important;
  }

  &:not(.v-text-field--outlined) :deep(input) {
    padding-top: 10px;
  }

  :deep(input) {
    text-overflow: ellipsis;
  }

  :deep(.v-input__icon--clear) .v-icon {
    opacity: 0;
    font-size: 18px !important;
  }

  &:hover :deep(.v-input__icon--clear) .v-icon {
    opacity: 1;
  }

  // Default style (matches MTextField solo look)
  &.m-default-style {
    :deep(.v-input__slot) {
      padding: 0px 8px !important;
      min-height: 32px !important;
      border-radius: 5px !important;
      background: rgba(255, 255, 255, 0.12) !important;
      border: none !important;
      font-size: 13px;
    }
    :deep(.v-input__slot::before),
    :deep(.v-input__slot::after) {
      display: none;
    }
    :deep(.v-input__append-inner) {
      align-self: center;
      margin-top: 0px !important;
      .v-icon {
        font-size: 24px !important;
        margin-top: 0 !important;
      }
    }
    :deep(.v-input__prepend-outer) {
      margin-top: 7px;
    }
    :deep(.v-label) {
      display: none;
    }
    :deep(.v-input__control) {
      min-height: 32px !important;
    }
    :deep(fieldset) {
      border: none !important;
    }
    :deep(input) {
      padding: 0 !important;
      max-height: 32px !important;
    }
    :deep(.v-select__selections) {
      padding: 0 4px !important;
      min-height: 32px !important;
      input {
        padding: 0 !important;
      }
    }
    &.v-input--is-focused :deep(.v-input__slot) {
      outline: 1px solid var(--v-primary-base) !important;
    }
    &.error--text :deep(.v-input__slot) {
      outline: 1px solid var(--v-error-base) !important;
    }
  }
}

.m-default-style-label {
  margin-bottom: 4px;
}

.v-input--is-focused:not(.v-input--is-readonly):not(.v-text-field--outlined):not(.m-default-style) {
  :deep(.v-input__slot) {
    outline: 1px solid var(--v-primary-base) !important;
  }
  :deep(.v-input__prepend-outer) .v-icon {
    color: var(--v-primary-base) !important;
  }
  :deep(.v-input__append-inner) .v-icon {
    color: var(--v-primary-base) !important;
  }
}

.m-combobox-menu-active {
  :deep(.v-input__slot) {
    outline: 1px solid var(--v-primary-base) !important;
  }
  :deep(.v-input__prepend-outer) .v-icon {
    color: var(--v-primary-base) !important;
  }
}

.v-input.v-input--dense {
  :deep(.v-select__selections input) {
    font-size: 12px !important;
  }
}

.m-combobox {
  :deep(.v-input__append-inner) {
    cursor: pointer;
  }
}

.m-combobox:not(.m-common):not(.m-default-style):not(.v-text-field--outlined) {
  :deep(.v-input__slot) {
    background: transparent !important;
    border: none !important;
    outline: none !important;
    padding: 0 !important;
    font-size: 16px;
  }

  margin-top: 16px !important;
  margin-bottom: 16px;
}

.m-combobox.m-common.v-text-field--outlined {
  :deep(fieldset) {
    border: none !important;
  }
  &.v-input--is-focused :deep(fieldset) {
    border: 2px solid var(--v-primary-base) !important;
  }
  &.error--text :deep(fieldset) {
    border: 2px solid var(--v-error-base) !important;
  }
}

.m-combobox-inline-label {
  margin-bottom: 4px;

  :deep(.v-input__slot) {
    border: none !important;
    padding: 0 8px !important;
  }

  :deep(.v-text-field__details) {
    padding: 0 !important;
  }

  :deep(fieldset) {
    border: none !important;
  }
}
</style>
